// floor-stack state machine — see floor-stack.md §11.
//
// states: idle → hovering → expanding → expanded → collapsing → idle
// events: HOVER, UNHOVER, CLICK(floorId), BACK, DATA_LOADED, DATA_ERROR
//
// We use a discriminated union state object instead of XState because
// the transitions are flat enough that a 30-line reducer is clearer than
// pulling a runtime dependency.

export type FloorStackState =
  | { kind: "idle" }
  | { kind: "hovering"; floorId: string }
  | { kind: "expanding"; floorId: string; startedAt: number }
  | { kind: "expanded"; floorId: string }
  | { kind: "collapsing"; floorId: string; startedAt: number }
  | { kind: "error"; message: string };

export type FloorStackEvent =
  | { type: "HOVER"; floorId: string }
  | { type: "UNHOVER" }
  | { type: "CLICK"; floorId: string }
  | { type: "EXPAND_FINISHED" }
  | { type: "BACK" }
  | { type: "COLLAPSE_FINISHED" }
  | { type: "DATA_ERROR"; message: string }
  | { type: "RESET" };

export const initial: FloorStackState = { kind: "idle" };

export function reduce(state: FloorStackState, ev: FloorStackEvent): FloorStackState {
  switch (ev.type) {
    case "RESET":
      return { kind: "idle" };
    case "DATA_ERROR":
      return { kind: "error", message: ev.message };
    case "HOVER":
      // Only allow hover transitions from idle / hovering — never while expanding or expanded.
      if (state.kind === "idle" || state.kind === "hovering") {
        return { kind: "hovering", floorId: ev.floorId };
      }
      return state;
    case "UNHOVER":
      if (state.kind === "hovering") return { kind: "idle" };
      return state;
    case "CLICK":
      // Click → start expanding. Disallow re-click while already expanding /
      // expanded / collapsing to avoid overlapping animations.
      if (state.kind === "idle" || state.kind === "hovering") {
        return { kind: "expanding", floorId: ev.floorId, startedAt: Date.now() };
      }
      return state;
    case "EXPAND_FINISHED":
      if (state.kind === "expanding") return { kind: "expanded", floorId: state.floorId };
      return state;
    case "BACK":
      if (state.kind === "expanded") return { kind: "collapsing", floorId: state.floorId, startedAt: Date.now() };
      return state;
    case "COLLAPSE_FINISHED":
      if (state.kind === "collapsing") return { kind: "idle" };
      return state;
    default:
      return state;
  }
}

/** Animation timings (ms). prefers-reduced-motion → snap to 0. */
export const TIMINGS = {
  hoverInMs: 180,
  hoverOutMs: 220,
  expandMs: 480,
  collapseMs: 380,
  staggerMs: 35,
} as const;

export function reducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
